import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { ApiService } from '../../../../../../core/services/api.service';
import {
  CityParkingRegisterDetailMemberGroupPayload,
  CityParkingRegisterDetailMemberGroupTable,
  CityParkingVehicleLineResponse,
  MemberVehiclesPayload,
  ParkingFeeForMemberResponse,
  RegisterRenewVehicleLineResponse,
} from '../../../../../../core/models/register-parking';
import { ModalLicenseAddComponent } from '../../../other/modal-license-add/modal-license-add.component';
import { CityParkingUserLineResponse } from '../../../../../../core/models/lineOA.model';
import { CookieService } from 'ngx-cookie-service';
import {
  addDaysToDateInput,
  localTodayDateInput,
  toLocalDateInput,
} from '../../../../../../core/utils/date-input.util';
import { normalizeParkingFeeForMemberList } from '../../../../../../core/utils/parking-fee-for-member.util';

@Component({
  selector: 'app-modal-register-parking-form-add',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalLicenseAddComponent],
  templateUrl: './modal-register-parking-form-add.component.html',
  styleUrl: './modal-register-parking-form-add.component.css',
})
export class ModalRegisterParkingFormAddComponent implements OnInit {
  isOpen: boolean = true;
  @Input() statusType: string = 'new';
  @Input() siteId: number | null = null;
  @Input() customerId: number | null = null;
  @Input() vehicleTypeId: number | null = null;
  @Input() clientId: number | null = null;

  data: CityParkingRegisterDetailMemberGroupPayload = {
    groupId: 0,
    userId: 0,
    cpUserId: 0,
    memberStartDate: '',
    feeId: 0,
    fee: 0,
    siteId: 0,
    cardId: 0,
    memberVehiclesPayloads: [],
    uid: '',
    phone: '',
    subScriptionId: 0,
  };

  packages: ParkingFeeForMemberResponse[] = [];
  lines: CityParkingUserLineResponse[] = [];

  userId: number = 0;
  vehicleList: (CityParkingVehicleLineResponse | RegisterRenewVehicleLineResponse)[] = [];

  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<CityParkingRegisterDetailMemberGroupTable>();

  selectedVehicleId: number[] = [];
  selectedSubscriptionId = 0;
  searchVehicleTerm: string = '';
  selectedLine: CityParkingUserLineResponse | null = null;

  isLicenseModalOpen: boolean = false;
  openDropdown: string | null = null;
  isLoadingVehicles: boolean = false;
  isLoading: boolean = false;

  constructor(
    private toast: ToastService,
    private apiService: ApiService,
    private cookieService: CookieService,
  ) { }

  ngOnInit(): void {
    this.data.memberStartDate = localTodayDateInput();

    const userIdFromSession = sessionStorage.getItem('user_id');
    this.userId =
      parseInt(this.cookieService.get('user_id')) ||
      (userIdFromSession ? JSON.parse(userIdFromSession) : 0);

    this.getCityparkingLineParking();
    this.getCityparkingLinePackage();

    if (this.statusType === 'new' && this.customerId) {
      this.loadCustomerVehiclesForRegister();
    } else if (this.statusType === 'renew' && this.customerId) {
      this.loadRegisterRenewVehicles();
    }
  }

  get filteredVehicles() {
    let list = this.vehicleList || [];
    if (this.searchVehicleTerm) {
      const term = this.searchVehicleTerm.toLowerCase();
      list = list.filter(
        (v) =>
          (v.plateNumber || '').toLowerCase().includes(term) ||
          (v.provinceName || '').toLowerCase().includes(term) ||
          (this.isRenewVehicle(v) ? v.cardName : '').toLowerCase().includes(term) ||
          (this.isRenewVehicle(v) ? v.cardCode : '').toLowerCase().includes(term) ||
          (v.lineFullName || '').toLowerCase().includes(term),
      );
    }
    return list;
  }

  get isDataNotFound(): boolean {
    return this.packages.length === 0;
  }

  get canAddNewLicense(): boolean {
    if ((this.selectedLine?.cpUserId ?? 0) > 0) {
      return true;
    }
    const selected = this.getSelectedVehicles();
    if (selected.length === 0) {
      return false;
    }
    return !!this.resolveVehicleLineInfo(selected[0]);
  }

  private readField(raw: Record<string, unknown>, camel: string, pascal: string): unknown {
    return raw[camel] ?? raw[pascal];
  }

  private normalizeVehicleLine(
    raw: CityParkingVehicleLineResponse | RegisterRenewVehicleLineResponse | Record<string, unknown>,
  ): CityParkingVehicleLineResponse | RegisterRenewVehicleLineResponse {
    const r = raw as Record<string, unknown>;
    const base: CityParkingVehicleLineResponse = {
      vehicleId: Number(this.readField(r, 'vehicleId', 'VehicleId') ?? 0),
      plateNumber: String(this.readField(r, 'plateNumber', 'PlateNumber') ?? ''),
      provinceName: String(this.readField(r, 'provinceName', 'ProvinceName') ?? ''),
      vehicleTypeName: String(this.readField(r, 'vehicleTypeName', 'VehicleTypeName') ?? ''),
      cpUserId: Number(this.readField(r, 'cpUserId', 'CpUserId') ?? 0),
      uid: String(this.readField(r, 'uid', 'Uid') ?? ''),
      phone: String(this.readField(r, 'phone', 'Phone') ?? ''),
      lineFullName: String(this.readField(r, 'lineFullName', 'LineFullName') ?? ''),
    };

    const cardId = Number(this.readField(r, 'cardId', 'CardId') ?? 0);
    if (cardId > 0 || this.readField(r, 'cardCode', 'CardCode')) {
      return {
        ...base,
        cardId,
        cardCode: String(this.readField(r, 'cardCode', 'CardCode') ?? ''),
        cardName: String(this.readField(r, 'cardName', 'CardName') ?? ''),
        expireDate: (this.readField(r, 'expireDate', 'ExpireDate') as string | null) ?? null,
      };
    }

    return base;
  }

  private normalizeVehicleList(
    list: (CityParkingVehicleLineResponse | RegisterRenewVehicleLineResponse | Record<string, unknown>)[],
  ): (CityParkingVehicleLineResponse | RegisterRenewVehicleLineResponse)[] {
    return (list ?? []).map((item) => this.normalizeVehicleLine(item));
  }

  private mergeVehicleLineInfo(
    vehicle: CityParkingVehicleLineResponse | RegisterRenewVehicleLineResponse,
  ): CityParkingVehicleLineResponse | RegisterRenewVehicleLineResponse {
    const normalized = this.normalizeVehicleLine(vehicle);

    if (normalized.cpUserId && normalized.uid) {
      return normalized;
    }

    if (normalized.cpUserId) {
      const line = this.lines.find((l) => (l.cpUserId ?? 0) === normalized.cpUserId);
      if (line) {
        return {
          ...normalized,
          uid: normalized.uid || line.uid || '',
          phone: normalized.phone || line.phone || '',
          lineFullName: normalized.lineFullName || line.fullName || '',
        };
      }
    }

    if (normalized.uid) {
      const line = this.lines.find((l) => l.uid === normalized.uid);
      if (line) {
        return {
          ...normalized,
          cpUserId: normalized.cpUserId || line.cpUserId || 0,
          phone: normalized.phone || line.phone || '',
          lineFullName: normalized.lineFullName || line.fullName || '',
        };
      }
    }

    return normalized;
  }

  private resolveVehicleLineInfo(
    vehicle: CityParkingVehicleLineResponse | RegisterRenewVehicleLineResponse,
  ): { uid: string; cpUserId: number; phone: string } | null {
    const merged = this.mergeVehicleLineInfo(vehicle);
    const uid = merged.uid ?? '';
    const cpUserId = merged.cpUserId ?? 0;
    const phone = merged.phone ?? '';

    if (!uid || cpUserId <= 0) {
      return null;
    }

    return { uid, cpUserId, phone };
  }

  private vehiclesMissingLineInfo(): boolean {
    return this.vehicleList.some((vehicle) => !this.resolveVehicleLineInfo(vehicle));
  }

  private tryEnrichVehicleOwners(): void {
    if (!this.vehicleList.length) {
      return;
    }

    this.vehicleList = this.vehicleList.map((vehicle) => this.mergeVehicleLineInfo(vehicle));

    if (!this.vehiclesMissingLineInfo() || !this.lines.length || !this.customerId) {
      return;
    }

    this.enrichVehicleOwnersFromLines();
  }

  private enrichVehicleOwnersFromLines(): void {
    if (!this.customerId || !this.siteId || !this.vehicleTypeId) {
      return;
    }

    for (const line of this.lines) {
      const uid = line.uid ?? '';
      const cpUserId = line.cpUserId ?? 0;
      if (!uid || cpUserId <= 0) {
        continue;
      }

      const request$ =
        this.statusType === 'renew'
          ? this.apiService.getRegisterRenewVehicles(
            this.customerId,
            this.siteId,
            this.vehicleTypeId,
            this.userId,
            this.clientId || 0,
          )
          : this.apiService.getCityParkingVehiclesForRegisterNew(
            this.customerId,
            this.siteId,
            this.vehicleTypeId,
            uid,
          );

      request$.subscribe({
        next: (response) => {
          console.log('Customer vehicle : ', this.statusType, response);

          const normalized = this.normalizeVehicleList(response ?? []);
          const ownedVehicleIds = new Set(
            normalized
              .filter((vehicle) => {
                if (this.statusType === 'renew') {
                  return vehicle.uid === uid;
                }
                return true;
              })
              .map((vehicle) => vehicle.vehicleId),
          );

          this.vehicleList = this.vehicleList.map((vehicle) => {
            if (!ownedVehicleIds.has(vehicle.vehicleId)) {
              return vehicle;
            }

            return {
              ...this.mergeVehicleLineInfo(vehicle),
              cpUserId,
              uid,
              phone: vehicle.phone || line.phone || '',
              lineFullName: vehicle.lineFullName || line.fullName || '',
            };
          });
        },
        error: (error) => console.error('Enrich vehicle owners error:', error),
      });
    }
  }

  getSelectedVehicles(): (CityParkingVehicleLineResponse | RegisterRenewVehicleLineResponse)[] {
    return this.selectedVehicleId
      .map((id) => this.vehicleList.find((v) => v.vehicleId === id))
      .filter((v): v is CityParkingVehicleLineResponse | RegisterRenewVehicleLineResponse => !!v)
      .map((vehicle) => this.mergeVehicleLineInfo(vehicle));
  }

  resolveLineFromSelectedVehicles(): { uid: string; cpUserId: number; phone: string } | null {
    const selected = this.getSelectedVehicles();
    if (selected.length === 0) {
      return null;
    }

    const firstInfo = this.resolveVehicleLineInfo(selected[0]);
    if (!firstInfo) {
      return null;
    }

    const hasMixedLine = selected.some((vehicle) => {
      const info = this.resolveVehicleLineInfo(vehicle);
      return !info || info.uid !== firstInfo.uid;
    });

    if (hasMixedLine) {
      this.toast.error('กรุณาเลือกทะเบียนที่อยู่ภายใต้ LINE OA เดียวกัน');
      return null;
    }

    return firstInfo;
  }

  applyVehicleLineInfo(v: CityParkingVehicleLineResponse | RegisterRenewVehicleLineResponse) {
    const info = this.resolveVehicleLineInfo(v);
    if (!info) {
      return;
    }
    this.data.uid = info.uid;
    this.data.phone = info.phone;
  }

  get licenseModalCpUserId(): number {
    if ((this.selectedLine?.cpUserId ?? 0) > 0) {
      return this.selectedLine!.cpUserId!;
    }
    const selected = this.getSelectedVehicles();
    const info = selected[0] ? this.resolveVehicleLineInfo(selected[0]) : null;
    return info?.cpUserId ?? 0;
  }

  get renewSelectedExpireDate(): string | null {
    if (this.statusType !== 'renew' || this.selectedVehicleId.length === 0) {
      return null;
    }
    const vehicle = this.vehicleList.find((v) => v.vehicleId === this.selectedVehicleId[0]);
    return this.isRenewVehicle(vehicle) ? vehicle.expireDate : null;
  }

  get renewSelectedCount(): number {
    return this.statusType === 'renew' ? this.selectedVehicleId.length : 0;
  }

  isRenewVehicle(
    v: CityParkingVehicleLineResponse | RegisterRenewVehicleLineResponse | undefined,
  ): v is RegisterRenewVehicleLineResponse {
    return !!v && 'cardId' in v;
  }

  openLicenseModal() {
    if (!this.canAddNewLicense) {
      this.toast.error('กรุณาเลือกสมาชิก LINE OA หรือเลือกทะเบียนก่อนเพิ่มทะเบียนใหม่');
      return;
    }
    this.isLicenseModalOpen = true;
  }

  onLicenseAdded() {
    this.isLicenseModalOpen = false;
    if (this.statusType === 'new') {
      this.loadCustomerVehiclesForRegister(this.selectedLine?.uid || undefined);
      return;
    }
    this.loadRegisterRenewVehicles(this.selectedLine?.uid || undefined);
  }

  selectVehicle(v: CityParkingVehicleLineResponse | RegisterRenewVehicleLineResponse) {
    const vehicleId = v.vehicleId;

    if (this.selectedVehicleId.includes(vehicleId)) {
      this.selectedVehicleId = this.selectedVehicleId.filter((id) => id !== vehicleId);
      return;
    }

    if (this.selectedVehicleId.length > 0) {
      const existing = this.getSelectedVehicles();
      const firstInfo = existing[0] ? this.resolveVehicleLineInfo(existing[0]) : null;
      const newInfo = this.resolveVehicleLineInfo(v);
      if (firstInfo && newInfo && firstInfo.uid !== newInfo.uid) {
        this.toast.error('กรุณาเลือกทะเบียนที่อยู่ภายใต้ LINE OA เดียวกัน');
        return;
      }
      if (firstInfo && !newInfo) {
        this.toast.error('ไม่พบข้อมูล LINE OA ของทะเบียนที่เลือก');
        return;
      }
    }

    this.selectedVehicleId.push(vehicleId);
    this.applyVehicleLineInfo(v);

    if (
      this.statusType === 'renew' &&
      this.isRenewVehicle(v) &&
      v.expireDate &&
      this.selectedVehicleId.length === 1
    ) {
      const expireLocal = toLocalDateInput(v.expireDate);
      if (expireLocal) {
        this.data.memberStartDate = addDaysToDateInput(expireLocal, 1);
      }
    }
  }

  onChangeLine(line: CityParkingUserLineResponse) {
    if (!line) return;

    this.data.uid = line.uid;
    this.data.phone = line.phone;
    this.selectedVehicleId = [];
    this.data.cardId = 0;

    if (this.statusType === 'new') {
      this.loadCustomerVehiclesForRegister(line.uid || undefined);
    } else {
      this.loadRegisterRenewVehicles(line.uid || undefined);
    }
  }

  onChangePackage(event: Event) {
    const target = event.target as HTMLSelectElement;
    this.selectedSubscriptionId = Number(target.value) || 0;
  }

  onSave() {
    if (!this.data.memberStartDate) return this.toast.error('กรุณาระบุวันเริ่มใช้งาน');
    if (!this.selectedSubscriptionId) return this.toast.error('กรุณาเลือกแพ็คเกจ');

    if (this.selectedVehicleId.length === 0) {
      return this.toast.error('กรุณาเลือกทะเบียนรถ');
    }

    let lineInfo = this.resolveLineFromSelectedVehicles();
    if (!lineInfo) {
      this.vehicleList = this.vehicleList.map((vehicle) => this.mergeVehicleLineInfo(vehicle));
      lineInfo = this.resolveLineFromSelectedVehicles();
    }
    if (!lineInfo) {
      return this.toast.error('ไม่พบข้อมูล LINE OA ของทะเบียนที่เลือก');
    }

    this.emitSaveItem(lineInfo);
  }

  private emitSaveItem(lineInfo: { uid: string; cpUserId: number; phone: string }) {
    const selectedVehicles = this.getSelectedVehicles();

    if (this.statusType === 'renew') {
      const invalidRenew = selectedVehicles.some(
        (vehicle) => !this.isRenewVehicle(vehicle) || vehicle.cardId <= 0,
      );
      if (invalidRenew) {
        return this.toast.error('กรุณาเลือกทะเบียนรถที่ต้องการต่ออายุ');
      }
    }

    const selectedPackage = this.packages.find((p) => p.id === this.selectedSubscriptionId);
    const feeAmount = selectedPackage ? selectedPackage.amount : 0;

    const vehiclesPayload: MemberVehiclesPayload[] = [];
    this.selectedVehicleId.forEach((id) => {
      const vehicle = this.vehicleList.find((x) => x.vehicleId == id);
      if (vehicle) {
        vehiclesPayload.push({
          vehicleId: id,
          license: vehicle.plateNumber || '',
          province: vehicle.provinceName || 'Bangkok',
          vehicleTypeId: String(this.vehicleTypeId || 0),
        });
      }
    });

    const firstRenewCard =
      this.statusType === 'renew' && this.isRenewVehicle(selectedVehicles[0])
        ? selectedVehicles[0].cardId
        : 0;

    const item: CityParkingRegisterDetailMemberGroupTable = {
      groupId: this.data.groupId,
      userId: this.userId,
      cpUserId: lineInfo.cpUserId,
      uid: lineInfo.uid,
      phone: lineInfo.phone,
      fee: feeAmount,
      memberStartDate: this.data.memberStartDate,
      feeId: selectedPackage?.feeId || 0,
      feeName: selectedPackage?.feeName || '',
      siteId: this.siteId || 0,
      cardId: this.statusType === 'renew' ? firstRenewCard : this.data.cardId,
      subScriptionId: selectedPackage?.id || 0,
      vat: selectedPackage?.vat ?? false,
      vatIncluded: selectedPackage?.vatIncluded ?? false,
      memberVehiclesPayloads: vehiclesPayload,
    };

    this.save.emit(item);
    this.closeModal();
  }

  closeModal() {
    this.close.emit();
    this.selectedVehicleId = [];
    this.searchVehicleTerm = '';
    this.selectedSubscriptionId = 0;
    this.data.userId = 0;
    this.data.cardId = 0;
  }

  toggleDropdown(key: string, event: Event) {
    event.stopPropagation();
    this.openDropdown = this.openDropdown === key ? null : key;
  }

  closeDropdowns() {
    this.openDropdown = null;
  }

  selectOption(key: string, value: any) {
    if (key === 'lines') {
      const line = this.lines.find((l) => l.id === value || l.cpUserId === value);
      if (line) {
        this.selectedLine = line;
        this.onChangeLine(line);
      }
    } else if (key === 'packages') {
      this.selectedSubscriptionId = Number(value) || 0;
    }

    this.openDropdown = null;
  }

  getOptionName(key: string, value: any): string {
    if (!value && value !== 0) return '';
    if (key === 'lines') {
      const line = this.lines.find((l) => l.id === value || l.cpUserId === value);
      return line ? line.fullName : '';
    }
    if (key === 'packages') {
      const pkg = this.packages.find((p) => p.id === value);
      return pkg ? `${pkg.feeName} (${pkg.amount.toLocaleString()} ฿)` : '';
    }
    return '';
  }

  get lineOptions() {
    return this.lines.map((l) => ({
      label: l.fullName,
      value: l.id || l.cpUserId,
    }));
  }

  get packageOptions() {
    return this.packages.map((p) => ({
      label: `${p.feeName} (${p.amount.toLocaleString()} ฿)`,
      value: Number(p.id),
    }));
  }

  getCityparkingLineParking() {
    this.isLoading = true;
    this.apiService
      .getCityparkingUserLineParking(this.customerId ? this.customerId.toString() : '')
      .subscribe({
        next: (response: CityParkingUserLineResponse[]) => {
          this.lines = (response ?? []).map((item) => {
            const raw = item as CityParkingUserLineResponse & Record<string, unknown>;
            return {
              ...item,
              cpUserId: Number(raw.cpUserId ?? raw['CpUserId'] ?? 0),
              fullName: String(raw.fullName ?? raw.name ?? raw['FullName'] ?? raw['Name'] ?? ''),
              uid: String(raw.uid ?? raw['Uid'] ?? ''),
              phone: String(raw.phone ?? raw['Phone'] ?? ''),
            };
          });
          this.isLoading = false;
          this.tryEnrichVehicleOwners();
        },
        error: (error) => {
          console.error('API error:', error);
          this.isLoading = false;
        },
      });
  }

  getCityparkingLinePackage() {
    this.isLoading = true;
    this.apiService.getParkingFeeForMemberApi(this.siteId || 0, this.vehicleTypeId || 0).subscribe({
      next: (response: ParkingFeeForMemberResponse[]) => {
        if (response.length === 0) {
          this.toast.error('ไม่พบแพ็คเกจในสถานที่นี้');
          this.isLoading = false;
          return;
        }
        this.packages = normalizeParkingFeeForMemberList(response);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('API error:', error);
        this.isLoading = false;
      },
    });
  }

  loadCustomerVehiclesForRegister(uid?: string) {
    if (!this.customerId) {
      this.vehicleList = [];
      return;
    }

    this.isLoadingVehicles = true;
    this.apiService
      .getCityParkingVehiclesForRegisterNew(
        this.customerId,
        this.siteId || 0,
        this.vehicleTypeId || 0,
        uid,
      )
      .subscribe({
        next: (response: CityParkingVehicleLineResponse[]) => {
          this.vehicleList = this.normalizeVehicleList(response ?? []);
          this.selectedVehicleId = this.selectedVehicleId.filter((id) =>
            this.vehicleList.some((v) => v.vehicleId === id),
          );
          this.isLoadingVehicles = false;
          this.tryEnrichVehicleOwners();
        },
        error: (error) => {
          console.error('API error:', error);
          this.vehicleList = [];
          this.isLoadingVehicles = false;
        },
      });
  }

  loadRegisterRenewVehicles(uid?: string) {
    if (!this.customerId || !this.siteId || !this.vehicleTypeId || !this.clientId) {
      this.vehicleList = [];
      return;
    }

    this.isLoadingVehicles = true;
    this.selectedVehicleId = [];
    this.data.cardId = 0;

    this.apiService
      .getRegisterRenewVehicles(
        this.customerId,
        this.siteId,
        this.vehicleTypeId,
        this.userId,
        this.clientId,
      )
      .subscribe({
        next: (response: RegisterRenewVehicleLineResponse[]) => {
          let list = this.normalizeVehicleList(response ?? []) as RegisterRenewVehicleLineResponse[];
          if (uid) {
            list = list.filter((v) => v.uid === uid);
          }
          this.vehicleList = list;
          if (this.vehicleList.length === 0) {
            this.toast.error('ไม่พบบัตรจอดรถที่สามารถต่ออายุได้ ณ สถานที่นี้');
          }
          this.isLoadingVehicles = false;
          this.tryEnrichVehicleOwners();
        },
        error: (error) => {
          console.error('API error:', error);
          this.vehicleList = [];
          this.isLoadingVehicles = false;
          this.toast.error('เกิดข้อผิดพลาดในการดึงข้อมูลทะเบียนสำหรับต่ออายุ');
        },
      });
  }
}
